/**
 * Utility to fetch and cache the default avatar image
 * This ensures the avatar is consistent across the application
 */

import { constant } from "../../components/Constants";
const DEFAULT_AVATAR_STORAGE_KEY = constant.DEFAULT_AVATAR_STORAGE_KEY;
const DEFAULT_AVATAR_URL = constant.DEFAULT_AVATAR_URL;
/**
 * Fetches and caches the default avatar as a blob in localStorage
 * @returns {Promise<string>} The cached avatar URL
 */
export const fetchAndCacheDefaultAvatar = async () => {
  try {
    // Check if we already have the default avatar stored
    const storedAvatar = localStorage.getItem(DEFAULT_AVATAR_STORAGE_KEY);
    if (storedAvatar) {
      return storedAvatar;
    }
   
    // Fetch the default avatar image
    const response = await fetch(DEFAULT_AVATAR_URL);
    if (!response.ok) throw new Error("Failed to fetch default avatar");
    
    const blob = await response.blob();
    
    // Convert blob to data URL
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        // Store in localStorage for future use
        localStorage.setItem(DEFAULT_AVATAR_STORAGE_KEY, dataUrl);
       
        resolve(dataUrl);
      };
      reader.onerror = (e) => {
        console.error("Error reading avatar blob:", e);
        reject(DEFAULT_AVATAR_URL);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error caching default avatar:", error);
    return DEFAULT_AVATAR_URL;
  }
};

/**
 * Gets the appropriate avatar URL (user's custom one or cached default)
 * @param {Object} user - User object with ProfilePicture property
 * @returns {string} - The avatar URL to use
 */
export const getAvatarUrl = (user) => {
  if (user?.ProfilePicture && user.ProfilePicture !== "null") {
    return user.ProfilePicture;
  }
  
  // If no profile picture, use the cached default avatar from localStorage
  const cachedDefaultAvatar = localStorage.getItem(DEFAULT_AVATAR_STORAGE_KEY);
  if (cachedDefaultAvatar) {
    return cachedDefaultAvatar;
  }
  
  // If no cached avatar either, use the default URL
  return DEFAULT_AVATAR_URL;
};

export { DEFAULT_AVATAR_STORAGE_KEY, DEFAULT_AVATAR_URL };